import {
  Accessor,
  batch,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
} from 'solid-js';
import { debounce } from '@solid-primitives/scheduled';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import { CapabilityCoordinatorBase } from '@trimble/web3d';

/**
 * Per-pane override of a global transparency level. While `linked` is
 * true the row mirrors the upstream global value; the moment the user
 * moves the slider, {@link setLocal} flips `linked` to false and the
 * row diverges. Clicking the relink affordance restores `linked` to
 * true; `local` is preserved so a future unlink can restore the user's
 * prior choice (overwritten on the next slider movement).
 */
export interface LinkableSliderState {
  readonly local: Accessor<number | null>;
  readonly linked: Accessor<boolean>;
  readonly effective: Accessor<number | null>;
  setLocal(value: number | null): void;
  relink(): void;
}

/**
 * Per-pane override of a continuous scalar that has (or will have) a
 * global upstream. Today only clip distance uses this; semantics
 * mirror {@link LinkableSliderState} but the value space is the
 * caller's domain (e.g. metres).
 */
export interface LinkableRangeState {
  readonly local: Accessor<number>;
  readonly linked: Accessor<boolean>;
  readonly effective: Accessor<number>;
  readonly effectiveDebounced: Accessor<number>;
  setLocal(value: number): void;
  relink(): void;
}

/**
 * Persisted snapshot of a single linkable slider row. Captures both
 * `linked` and `local` even when `linked` is true so the unlink → move
 * → relink → unlink cycle can restore the user's prior choice across
 * save/load (matches the in-memory semantics described on
 * {@link LinkableSliderState}).
 */
export interface LinkableSliderSnapshot {
  readonly local: number | null;
  readonly linked: boolean;
}

/** Persisted snapshot of {@link LinkableRangeState}; see {@link LinkableSliderSnapshot}. */
export interface LinkableRangeSnapshot {
  readonly local: number;
  readonly linked: boolean;
}

/**
 * Serialisable snapshot of the per-pane visibility state, suitable for
 * stashing in multipane reserved view data. Companion to
 * {@link PaneVisibilityCoordinator.getSnapshot} /
 * {@link PaneVisibilityCoordinator.applySnapshot}.
 */
export interface PaneVisibilitySnapshot {
  readonly models: LinkableSliderSnapshot;
  readonly pointClouds: LinkableSliderSnapshot;
  readonly stations: LinkableSliderSnapshot;
  readonly image: LinkableSliderSnapshot;
  readonly clipDistance: LinkableRangeSnapshot;
}

/**
 * Per-pane visibility state for the floating toolbar dropdown. Holds local
 * overrides + link flags for every linkable row; the row config
 * factory (see `build-pane-visibility-rows.ts`) decides which subset to
 * surface per view mode. Toggles (frustum / station markers / nav
 * path / image / point-cloud on-off) stay on their underlying
 * capabilities — this class owns only the linkable slider/range state.
 *
 * Naming: coordinator rather than capability because it extends
 * `CapabilityCoordinatorBase` and has no `CapabilityCategory` /
 * mutual-exclusion contract; it just owns cross-cutting linkable state
 * consumed by the main-toolbar and per-pane visibility dropdowns.
 */
export class PaneVisibilityCoordinator extends CapabilityCoordinatorBase<unknown> {
  public readonly models: LinkableSliderState;
  public readonly pointClouds: LinkableSliderState;
  public readonly stations: LinkableSliderState;
  /**
   * Planar-mode image opacity. There is no global "image" transparency
   * upstream (unlike `models` / `pointClouds` / `stations`), so this row
   * is effectively local-only: while `linked` it reports a null
   * (fully-opaque) effective value, and the moment the user drags the
   * slider it diverges to a concrete local percentage. The sync-to-global
   * affordance is intentionally not surfaced for this row by the UI.
   */
  public readonly image: LinkableSliderState;
  public readonly clipDistance: LinkableRangeState;

  private readonly applyModelsSnapshot: (s: LinkableSliderSnapshot) => void;
  private readonly applyPointCloudsSnapshot: (
    s: LinkableSliderSnapshot
  ) => void;
  private readonly applyStationsSnapshot: (s: LinkableSliderSnapshot) => void;
  private readonly applyImageSnapshot: (s: LinkableSliderSnapshot) => void;
  private readonly applyClipDistanceSnapshot: (
    s: LinkableRangeSnapshot
  ) => void;

  constructor(
    viewsProvider: Accessor<ViewsCapabilityProvider | undefined>,
    upstreamClipDistance: Accessor<number>
  ) {
    // The base class requires a "registry" reference but we don't use it
    // — this capability only proxies upstream signals through link state.
    super(undefined);
    const models = createLinkableSlider(
      () => viewsProvider()?.getGlobalModelTransparencyLevel() ?? null
    );
    this.models = models.state;
    this.applyModelsSnapshot = models.applySnapshot;

    const pointClouds = createLinkableSlider(
      () => viewsProvider()?.getGlobalPointCloudTransparencyLevel() ?? null
    );
    this.pointClouds = pointClouds.state;
    this.applyPointCloudsSnapshot = pointClouds.applySnapshot;

    const stations = createLinkableSlider(
      () => viewsProvider()?.getGlobalStationTransparencyLevel() ?? null
    );
    this.stations = stations.state;
    this.applyStationsSnapshot = stations.applySnapshot;

    const image = createLinkableSlider(() => null);
    this.image = image.state;
    this.applyImageSnapshot = image.applySnapshot;

    const clipDistance = createLinkableRange(upstreamClipDistance);
    this.clipDistance = clipDistance.state;
    this.applyClipDistanceSnapshot = clipDistance.applySnapshot;
  }

  /** Capture the current state for persistence. */
  public getSnapshot(): PaneVisibilitySnapshot {
    return {
      models: snapshotSlider(this.models),
      pointClouds: snapshotSlider(this.pointClouds),
      stations: snapshotSlider(this.stations),
      image: snapshotSlider(this.image),
      clipDistance: snapshotRange(this.clipDistance),
    };
  }

  /**
   * Restore a previously {@link getSnapshot}-captured state. Writes
   * `local` and `linked` directly (bypassing the auto-unlink in
   * {@link LinkableSliderState.setLocal}) so a snapshot with
   * `linked: true` survives the round-trip. All writes batch into a
   * single reactive update.
   */
  public applySnapshot(snapshot: PaneVisibilitySnapshot): void {
    batch(() => {
      this.applyModelsSnapshot(snapshot.models);
      this.applyPointCloudsSnapshot(snapshot.pointClouds);
      this.applyStationsSnapshot(snapshot.stations);
      this.applyImageSnapshot(snapshot.image);
      this.applyClipDistanceSnapshot(snapshot.clipDistance);
    });
  }

  // Signals dispose with the owning createRoot; nothing to release here.
  public async cleanup(): Promise<void> {
    // intentionally empty
  }
}

const snapshotSlider = (
  state: LinkableSliderState
): LinkableSliderSnapshot => ({
  local: state.local(),
  linked: state.linked(),
});

const snapshotRange = (state: LinkableRangeState): LinkableRangeSnapshot => ({
  local: state.local(),
  linked: state.linked(),
});

interface LinkableSliderBundle {
  readonly state: LinkableSliderState;
  /** Restore `local` + `linked` atomically without the {@link LinkableSliderState.setLocal} auto-unlink. */
  applySnapshot(snapshot: LinkableSliderSnapshot): void;
}

interface LinkableRangeBundle {
  readonly state: LinkableRangeState;
  applySnapshot(snapshot: LinkableRangeSnapshot): void;
}

const CLIP_DISTANCE_DEBOUNCE_MS = 250;

function createDebouncedAccessor<T>(
  source: Accessor<T>,
  delayMs: number
): Accessor<T> {
  const [debounced, setDebounced] = createSignal<T>(source());
  const trigger = debounce((value: T) => setDebounced(() => value), delayMs);
  createEffect(() => trigger(source()));
  onCleanup(() => trigger.clear());
  return debounced;
}

function createLinkableSlider(
  upstream: Accessor<number | null>
): LinkableSliderBundle {
  const [local, setLocalSignal] = createSignal<number | null>(null);
  const [linked, setLinked] = createSignal<boolean>(true);

  const effective = createMemo<number | null>(() =>
    linked() ? upstream() : local()
  );

  return {
    state: {
      local,
      linked,
      effective,
      setLocal: (value: number | null): void => {
        batch(() => {
          setLocalSignal(value);
          setLinked(false);
        });
      },
      relink: (): void => {
        setLinked(true);
      },
    },
    applySnapshot: (snapshot: LinkableSliderSnapshot): void => {
      batch(() => {
        setLocalSignal(snapshot.local);
        setLinked(snapshot.linked);
      });
    },
  };
}

function createLinkableRange(
  upstream: Accessor<number>,
  debounceMs: number = CLIP_DISTANCE_DEBOUNCE_MS
): LinkableRangeBundle {
  const [local, setLocalSignal] = createSignal<number>(upstream());
  const [linked, setLinked] = createSignal<boolean>(true);

  const effective = createMemo<number>(() => (linked() ? upstream() : local()));
  // eslint-disable-next-line solid/reactivity -- `effective` is tracked inside the createEffect within createDebouncedAccessor; the rule can't see through the indirection
  const effectiveDebounced = createDebouncedAccessor(effective, debounceMs);

  return {
    state: {
      local,
      linked,
      effective,
      effectiveDebounced,
      setLocal: (value: number): void => {
        batch(() => {
          setLocalSignal(value);
          setLinked(false);
        });
      },
      relink: (): void => {
        setLinked(true);
      },
    },
    applySnapshot: (snapshot: LinkableRangeSnapshot): void => {
      batch(() => {
        setLocalSignal(snapshot.local);
        setLinked(snapshot.linked);
      });
    },
  };
}
